import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV } from '../data/mock';
import { ShoppingBag, Menu, X } from 'lucide-react';

const SECTION_LINKS = [
  { label: 'The Trial', target: 'what' },
  { label: 'History', target: 'history' },
  { label: 'The Cottage', target: 'where' },
  { label: 'Book', target: 'book' }
];

const Nav = ({ onBookClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const isPress = pathname.startsWith('/press');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (!isHome) {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBook = () => (isHome && onBookClick ? onBookClick() : goTo('book'));

  const linkClass = (active) =>
    `text-[12px] tracking-[0.28em] uppercase transition-colors duration-300 font-fell-sc ${
      active ? 'text-[#e8dcc0]' : 'text-[#a89880] hover:text-[#e8dcc0]'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
        scrolled || !isHome
          ? 'bg-[#0a0705]/90 backdrop-blur-sm border-b border-[#3a2818]/40'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <button
          onClick={() => goTo('top')}
          className="flex items-baseline gap-2 group"
          aria-label="ASH home"
          data-testid="nav-home-button"
        >
          <span className="font-display text-2xl md:text-[26px] tracking-[0.35em] text-[#e8dcc0] group-hover:text-[#d9a55e] transition-colors">
            {NAV.title}
          </span>
          <span className="hidden sm:inline text-[10px] md:text-[11px] tracking-[0.32em] text-[#8b7355]">
            {NAV.subtitle}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {SECTION_LINKS.map((l) => (
            <button
              key={l.target}
              onClick={() => goTo(l.target)}
              data-testid={`nav-link-${l.target}`}
              className={linkClass(false)}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/press"
            onClick={() => setOpen(false)}
            data-testid="nav-link-press"
            className={linkClass(isPress)}
          >
            Press
          </Link>
          <button
            onClick={handleBook}
            aria-label="Cart"
            data-testid="nav-cart-button"
            className="text-[#a89880] hover:text-[#d9a55e] transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
          </button>
        </nav>

        <button
          className="md:hidden text-[#e8dcc0]"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="nav-mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0a0705]/98 border-t border-[#3a2818]/40 px-6 py-6 flex flex-col gap-5">
          {SECTION_LINKS.map((l) => (
            <button
              key={l.target}
              onClick={() => goTo(l.target)}
              data-testid={`nav-mobile-link-${l.target}`}
              className="text-left text-[13px] tracking-[0.3em] uppercase text-[#a89880] font-fell-sc"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/press"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-link-press"
            className={`text-left text-[13px] tracking-[0.3em] uppercase font-fell-sc ${
              isPress ? 'text-[#e8dcc0]' : 'text-[#a89880]'
            }`}
          >
            Press
          </Link>
        </div>
      )}
    </header>
  );
};

export default Nav;
